/**
 * The confirmation of an action, in place of `window.confirm`: a modal `<dialog>` in plane's
 * style, opened by `confirmAction` and resolved with the user's answer.
 *
 * Built on the native dialog element and `showModal()`: the page behind is inert, focus stays
 * inside, and Escape cancels. The element is appended to the body for one question and removed
 * once answered, so the page that asked can re-render, or go away, in the meantime.
 */
import { LitElement, html } from 'lit';

import { formStyles } from '../../styles/shared/forms.js';
import { planeConfirmStyles } from '../../styles/components/plane-confirm.js';

class PlaneConfirm extends LitElement {
  static properties = {
    heading: { type: String },
    message: { type: String },
    confirmLabel: { type: String },
    tone: { type: String },
  };

  static styles = [
    formStyles,
    planeConfirmStyles,
  ];

  constructor() {
    super();
    this.heading = '';
    this.message = '';
    this.confirmLabel = 'Confirm';
    this.tone = 'primary';
    this.answered = null;
  }

  firstUpdated() {
    this.renderRoot.querySelector('dialog').showModal();
  }

  /** The dialog closes with Escape too: that, like Cancel, is a no. */
  closed(event) {
    this.answered?.(event.target.returnValue === 'confirm');
    this.answered = null;
    this.remove();
  }

  render() {
    return html`
      <dialog aria-labelledby="heading" aria-describedby="message" @close=${(event) => this.closed(event)}>
        <form method="dialog">
          <h2 id="heading">${this.heading}</h2>
          <p id="message">${this.message}</p>
          <div class="buttons">
            <button value="cancel" autofocus>Cancel</button>
            <button value="confirm" class="confirm ${this.tone}">${this.confirmLabel}</button>
          </div>
        </form>
      </dialog>
    `;
  }
}

customElements.define('plane-confirm', PlaneConfirm);

/**
 * Asks the user to confirm an action; resolves to true when they do.
 *
 * `tone` is `primary` for an ordinary action, `danger` for one that removes or interrupts
 * something. Cancel has the focus: Enter on an opened dialog never confirms by itself.
 *
 * @param {{ heading: string, message: string, confirmLabel: string, tone?: 'primary' | 'danger' }} question
 * @returns {Promise<boolean>}
 */
export function confirmAction({ heading, message, confirmLabel, tone = 'primary' }) {
  return new Promise((resolve) => {
    const dialog = document.createElement('plane-confirm');
    Object.assign(dialog, { heading, message, confirmLabel, tone, answered: resolve });
    document.body.append(dialog);
  });
}
